/* ═══════════════════════════════════════════════════════════════
   Admin Center — Roles, Bill Rates, Lookup Tables, CSV Import
   Shell/router. Split into src/renderer/pages/admin/* (pure extraction,
   no behavior change) — see git history for the original monolith.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { rem } from '../../lib/rem';
import { AdminService } from '../../services/adminService';
import { VacationService } from '../../services/vacationService';
import { hasPermission } from '../../../shared/permissions';
import { HarSyncTab } from '../HarSyncTab';
import { canHarSync } from '../../services/harSyncService';
import type { AdminPageProps, Tab } from './types';
import { TAB_GROUPS } from './types';
import { RolesTab } from './RolesTab';
import { BillingRolesTab } from './BillingRolesTab';
import { LookupTableTab } from './LookupTableTab';
import { ImportTab } from './ImportTab';
import { SubscriptionTab } from './SubscriptionTab';
import { ActivityLogTab } from './ActivityLogTab';
import { DepartmentsTab } from './DepartmentsTab';
import { ScopesTab } from './ScopesTab';
import { FirmHolidaysTab } from './FirmHolidaysTab';
import { ExpenseTypesTab } from './ExpenseTypesTab';

/* ═══════════ MAIN ADMIN PAGE ═══════════ */
function AdminPage({ toast, userPermissions, userEmail, enabledModules }: AdminPageProps) {
  // Filter groups to only the tabs the user has access to — each tab checks
  // its own permission (falling back to the group's) so Departments can be
  // gated separately from Roles & Privileges (R1.7) even though they share
  // a group.
  let visibleGroups = TAB_GROUPS.map((g) => ({
    ...g,
    tabs: g.tabs.filter((t) => {
      const perm = t.permission ?? g.permission;
      return !perm || hasPermission(userPermissions, perm);
    }),
  })).filter((g) => g.tabs.length > 0);
  // HAR Sync (RWB bridge) is account-gated, not permission-gated — it only
  // exists for Josh's account on desktop (canHarSync checks both).
  if (canHarSync({ email: userEmail })) {
    visibleGroups = visibleGroups.map((g) =>
      g.label === 'Data'
        ? { ...g, tabs: [...g.tabs, { id: 'harSync' as Tab, label: 'HAR Sync' }] }
        : g
    );
  }
  const allVisibleTabs = visibleGroups.flatMap((g) => g.tabs);
  const [tab, setTab] = useState<Tab>(allVisibleTabs[0]?.id || 'roles');

  // If current tab is no longer visible (permissions changed), reset to first visible
  const currentTabVisible = allVisibleTabs.some((t) => t.id === tab);
  if (!currentTabVisible && allVisibleTabs.length > 0 && tab !== allVisibleTabs[0].id) {
    setTab(allVisibleTabs[0].id);
  }

  return (
    <div style={{ padding: 'var(--space-5)', overflow: 'auto', height: '100%' }}>
      <div style={{ marginBottom: rem(20) }}>
        <h2 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 700, margin: 0 }}>
          Admin Center
        </h2>
        <p
          style={{
            fontSize: 'var(--font-size-base)',
            color: 'var(--tm)',
            margin: 'var(--space-1) 0 0',
          }}
        >
          Manage roles, rates, billing codes, fee structures, and import data
        </p>
      </div>

      {/* Grouped tab bar */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          marginBottom: rem(20),
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {visibleGroups.map((group, gi) => (
          <React.Fragment key={group.label}>
            {gi > 0 && (
              <div
                style={{ width: rem(1), height: rem(24), background: 'var(--bd)', flexShrink: 0 }}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: rem(2) }}>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.06em',
                  color: 'var(--tm)',
                  padding: '0 var(--space-1)',
                }}
              >
                {group.label}
              </span>
              <div
                style={{
                  display: 'flex',
                  gap: rem(2),
                  background: 'var(--sa)',
                  borderRadius: 'var(--radius-md)',
                  padding: rem(2),
                }}
              >
                {group.tabs.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    style={{
                      padding: `${rem(6)} ${rem(14)}`,
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 600,
                      cursor: 'pointer',
                      background: tab === t.id ? 'var(--sf)' : 'transparent',
                      color: tab === t.id ? 'var(--tx)' : 'var(--tm)',
                      boxShadow: tab === t.id ? 'var(--shadow-sm)' : 'none',
                      transition: 'all .15s',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'roles' && <RolesTab toast={toast} enabledModules={enabledModules} />}
      {tab === 'billingRoles' && <BillingRolesTab toast={toast} />}
      {tab === 'projectTypes' && (
        <LookupTableTab
          key="pt"
          title="Project Type"
          description="Categorize projects by type (e.g., Educational K-12, Municipal, Medical)."
          service={AdminService.projectTypes}
          tableName="project_types"
          toast={toast}
        />
      )}
      {tab === 'billingTypes' && (
        <LookupTableTab
          key="bt"
          title="Fee Structure"
          description="Define fee structures (e.g., Percent of Construction Cost, Time & Expense, Fixed Fee)."
          service={AdminService.billingTypes}
          tableName="billing_types"
          toast={toast}
        />
      )}
      {tab === 'activityCodes' && (
        <LookupTableTab
          key="ac"
          title="Billing Code"
          description="Billing codes for time tracking (e.g., HVAC, Electrical, Specifications)."
          service={AdminService.activityCodes}
          tableName="activity_codes"
          toast={toast}
        />
      )}
      {tab === 'phases' && (
        <LookupTableTab
          key="ph"
          title="Phase"
          description="Universal project phases. All projects share these phases (e.g., Schematic Design, Construction Documents). Change the order or add new phases here."
          service={AdminService.defaultPhases}
          tableName="default_phases"
          toast={toast}
        />
      )}
      {tab === 'overheadCategories' && (
        <LookupTableTab
          key="oc"
          title="Overhead Category"
          description="Non-billable overhead categories for timesheets (e.g., Holiday, PTO, Marketing)."
          service={AdminService.overheadCategories}
          tableName="overhead_categories"
          toast={toast}
        />
      )}
      {tab === 'vacationCategories' && (
        <LookupTableTab
          key="vc"
          title="Vacation Category"
          description="Leave types for vacation requests (e.g., PTO, Vacation, Sick, Personal). Employees select these when submitting time-off requests."
          service={VacationService.categories}
          tableName="vacation_categories"
          toast={toast}
        />
      )}
      {tab === 'firmHolidays' && <FirmHolidaysTab toast={toast} />}
      {tab === 'expenseTypes' && <ExpenseTypesTab toast={toast} />}
      {tab === 'scopes' && <ScopesTab toast={toast} />}
      {tab === 'departments' && <DepartmentsTab toast={toast} />}
      {tab === 'import' && <ImportTab toast={toast} />}
      {tab === 'harSync' && <HarSyncTab toast={toast} userPermissions={userPermissions} />}
      {tab === 'subscription' && (
        <SubscriptionTab toast={toast} userPermissions={userPermissions} />
      )}
      {tab === 'activity' && <ActivityLogTab toast={toast} />}
    </div>
  );
}

(window as any).AdminPage = AdminPage;
export { AdminPage };
